import React, { useState, useMemo } from 'react';
import { logAction } from '../../../utils/scoringLog';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { BackIcon } from '../../../components/AppHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { submitScore } from '../../../api';
import { useAuth } from '../../../auth/AuthContext';
import SignatureCapture from '../../../components/SignatureCapture';
import Snackbar from '../../../components/Snackbar';
import { camelCase } from 'lodash-es';
import { summarizeScore, pad } from '../../../utils/scoring';
import { CondensedScoreView } from './ScoreReview';

const CELL = {
  display: 'block', textAlign: 'center', lineHeight: '40px',
  width: 40, height: 40, margin: 2, border: '1px solid rgba(0,0,0,0.5)',
};
const CELL_BLANK = { ...CELL, border: '1px solid white' };
const HEADER = { ...CELL_BLANK, height: 20, lineHeight: '20px', fontSize: '0.75rem' };
const SUMMARY_CELL = {
  display: 'table-cell', textAlign: 'center', lineHeight: '40px',
  width: 43, height: 40, borderRight: '2px solid black', borderTop: '2px solid black', borderBottom: '2px solid black',
};
const CAPTION = { fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' };

export default function ScoreSignature() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stageIdx, squadId, shooterId } = useParams();
  const { matchId, match, stages, shooters, refreshScores, pendingScore, setPendingScore } = useOutletContext();
  const stageIdxNum = parseInt(stageIdx, 10);
  const basePath = `/${matchId}/manage/scores`;
  const scorePath = `${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode`;
  const [searchParams] = useSearchParams();
  const fromSearch = searchParams.get('fromSearch') || squadId === '_nosquad';
  const splitParam = searchParams.get('split');
  const backPath = fromSearch ? `${basePath}/stage/${stageIdx}` : `${scorePath}${splitParam ? `?split=${splitParam}` : ''}`;

  const stage = stages[stageIdxNum - 1];
  const shooter = shooters.find((s) => s.id === shooterId);
  const scoreMode = match?.scoreMode || 'comstock';
  const displayMode = match?.scoreFormDisplayMode || 'normal';

  const roSigKey = `__ro_signature_${matchId}`;
  const [roSignature, setRoSignatureRaw] = useState(() => localStorage.getItem(roSigKey) || '');
  const setRoSignature = (sig) => {
    setRoSignatureRaw(sig);
    if (sig) localStorage.setItem(roSigKey, sig);
  };
  const [shooterSignature, setShooterSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const summary = useMemo(() => {
    if (!pendingScore || pendingScore.isDq || !stage || !shooter) return null;
    return summarizeScore({
      score: {
        scorePerTarget: pendingScore.scorePerTarget || [],
        poppers: pendingScore.poppers || 0,
        tenPointsPoppers: pendingScore.tenPointsPoppers || 0,
        'no-shoots': pendingScore['no-shoots'] || 0,
        penaltiesByType: pendingScore.penaltiesByType || {},
        specialPenalty: pendingScore.specialPenalty || 0,
        time: pendingScore.time || 0,
        bonusField: pendingScore.bonusField || 0,
      },
      powerFactor: shooter['power-factor'],
      stage,
      mode: scoreMode,
    });
  }, [pendingScore, stage, scoreMode, shooter]);

  // Warning about misses
  const missWarning = useMemo(() => {
    if (!summary) return null;
    const totalMisses = summary.misses + (summary.bonusMisses || 0);
    const totalPenalties = summary.penalties || 0;
    if (totalMisses > 0 && totalPenalties > 0) return t('scoring.areSureMissesAndProcedures', { misses: totalMisses, penalties: totalPenalties });
    if (totalMisses > 0) return t('scoring.areSureMisses', { misses: totalMisses });
    if (totalPenalties > 0) return t('scoring.areSureProcedures', { penalties: totalPenalties });
    return null;
  }, [summary, t]);

  const handleSubmit = async () => {
    if (!roSignature) {
      setSnack({ open: true, message: t('scoring.roMissingSignature'), severity: 'error' });
      return;
    }
    if (!pendingScore.isDq && !shooterSignature) {
      setSnack({ open: true, message: t('scoring.missingSignature'), severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      let payload;
      if (pendingScore.isDq) {
        payload = {
          stageIdx: pendingScore.stageIdx,
          shooterId: pendingScore.shooterId,
          dq: pendingScore.dq,
          roSignature,
          occuredOn: pendingScore.occuredOn,
        };
      } else {
        payload = {
          stageIdx: pendingScore.stageIdx,
          shooterId: pendingScore.shooterId,
          scorePerTarget: pendingScore.scorePerTarget,
          poppers: pendingScore.poppers,
          tenPointsPoppers: pendingScore.tenPointsPoppers,
          'no-shoots': pendingScore['no-shoots'],
          penaltiesByType: pendingScore.penaltiesByType,
          specialPenalty: pendingScore.specialPenalty,
          bonusField: pendingScore.bonusField,
          time: pendingScore.time,
          roSignature,
          signature: shooterSignature,
        };
      }
      await submitScore(matchId, payload, { finalize: true });
      logAction('SCORE_SUBMITTED', { matchId, stageIdx: stageIdxNum, shooterId }, payload);
      await refreshScores();
      setPendingScore(null);
      localStorage.removeItem(`__scoring_entry_${matchId}_${stageIdx}`);
      setSnack({ open: true, message: t('scoring.sendSuccess'), severity: 'success' });
      navigate(backPath);
    } catch (err) {
      logAction('SCORE_SUBMIT_FAILED', { matchId, stageIdx: stageIdxNum, shooterId }, { error: err.response?.data?.error || err.message });
      setSnack({ open: true, message: err.response?.data?.error || t('scoring.sendError'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTempSave = async () => {
    if (!roSignature) {
      setSnack({ open: true, message: t('scoring.roMissingSignature'), severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        stageIdx: pendingScore.stageIdx,
        shooterId: pendingScore.shooterId,
        scorePerTarget: pendingScore.scorePerTarget,
        poppers: pendingScore.poppers,
        tenPointsPoppers: pendingScore.tenPointsPoppers,
        'no-shoots': pendingScore['no-shoots'],
        penaltiesByType: pendingScore.penaltiesByType,
        specialPenalty: pendingScore.specialPenalty,
        bonusField: pendingScore.bonusField,
        time: pendingScore.time,
        roSignature,
      };
      await submitScore(matchId, payload);
      await refreshScores();
      setPendingScore(null);
      localStorage.removeItem(`__scoring_entry_${matchId}_${stageIdx}`);
      navigate(backPath);
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('scoring.sendError'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingScore || !shooter || !stage) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">{t('scoring.noScoreData')}</Typography>
        <Button onClick={() => navigate(backPath)} sx={{ mt: 2 }}>{t('common.back')}</Button>
      </Box>
    );
  }

  const targets = stage.targets || {};
  const numPaper = (targets.paper || 0) + (targets.minipaper || 0);
  const numMetal = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0);
  const numTenPoints = (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
  const isDq = pendingScore.isDq;
  const poppers = pendingScore.poppers || 0;
  const tenPointsPoppers = pendingScore.tenPointsPoppers || 0;
  const scorePerTarget = pendingScore.scorePerTarget || [];
  const noShoots = pendingScore['no-shoots'] || 0;
  const penaltiesByType = pendingScore.penaltiesByType || {};
  const specialPenalty = pendingScore.specialPenalty || 0;
  const bonusField = pendingScore.bonusField || 0;
  const time = pendingScore.time || 0;
  const procTotal = Object.values(penaltiesByType).reduce((s, v) => s + v, 0);
  const metalMissesValue = scoreMode === 'steel-no-miss' ? '' : (!time ? '' : (numMetal - poppers) || '');
  const cLabel = scoreMode === 'comstock-abd' ? 'B' : 'C';

  const shooterInfo = [
    shooter.division ? t(`divisions.${camelCase(shooter.division)}`, shooter.division) : null,
    shooter.category ? t(`categories.${camelCase(shooter.category)}`, shooter.category) : null,
    shooter['power-factor'] ? t(`registration.${camelCase(shooter['power-factor'])}`, shooter['power-factor']) : null,
  ].filter(Boolean).join(' | ');
  const now = new Date();
  const formTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`${scorePath}/scoring/${shooterId}${splitParam ? `?split=${splitParam}` : ''}`, { state: { fromSignature: true } })}>
          {t('common.back')}
        </Button>
      </Box>

      {/* Score display + signatures */}
      {displayMode !== 'normal' ? (
        <div style={{ marginBottom: 24 }}>
          <CondensedScoreView
            shooter={shooter}
            stageIdx={stageIdx}
            stage={stage}
            score={{ ...pendingScore, 'no-shoots': pendingScore['no-shoots'] || 0, penaltiesByType: pendingScore.penaltiesByType || {} }}
            summary={summary}
            isDq={isDq}
            scoreMode={scoreMode}
            numTenPoints={numTenPoints}
            t={t}
            isRtl={i18n.dir() === 'rtl'}
          />
          {missWarning && (
            <div style={{ marginTop: 12, padding: 12, background: '#fff3e0', borderRadius: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>{missWarning}</Typography>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <SignatureCapture label={t('scoring.roSignature')} value={roSignature} onEnd={setRoSignature} />
            {!isDq && (
              <Box sx={{ mt: 2 }}>
                <SignatureCapture
                  label={`${t('scoring.signature')} - ${shooter.name}`}
                  value={shooterSignature}
                  onEnd={setShooterSignature}
                />
              </Box>
            )}
          </div>
        </div>
      ) : (
      <div style={{ background: 'white', boxShadow: '0px 0px 2px rgba(0,0,0,0.3)', padding: 12, margin: 'auto', marginBottom: 24, position: 'relative', direction: 'ltr', maxWidth: 500 }}>
        {/* DQ overlay */}
        {isDq && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, pointerEvents: 'none' }}>
            <div style={{ transform: 'rotate(-50deg)', fontSize: 64, textAlign: 'center' }}>
              <div style={{ fontSize: 24 }}>{shooter.publicId} / {shooter.name}</div>
              <div>DQ</div>
              <div>{pendingScore.dq}</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', opacity: isDq ? 0.5 : 1 }}>
          {/* Left: Score table */}
          <div>
            {/* Column headers: blank, A, C, D, M, (M) */}
            <div style={{ display: 'flex' }}>
              <div style={CELL_BLANK} />
              <div style={HEADER}>A</div>
              <div style={HEADER}>{cLabel}</div>
              <div style={HEADER}>D</div>
              <div style={HEADER}>M</div>
              <div style={{ ...HEADER, fontStyle: 'italic' }}>(M)</div>
            </div>

            {/* Metal targets row */}
            {numMetal > 0 && (
              <div style={{ display: 'flex' }}>
                <div style={CELL_BLANK} />
                <div style={CELL}>{poppers || ''}</div>
                <div style={{ ...CELL_BLANK, width: 82, height: 36, margin: 4, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t('results.poppers')}
                </div>
                <div style={CELL}>{metalMissesValue}</div>
                {scoreMode === 'steel-no-miss' && (
                  <div style={CELL}>{!time ? '' : (numMetal - poppers) || ''}</div>
                )}
              </div>
            )}

            {/* Ten points poppers row */}
            {numTenPoints > 0 && (
              <div style={{ display: 'flex' }}>
                <div style={CELL_BLANK} />
                <div style={CELL}>{tenPointsPoppers || ''}</div>
                <div style={{ ...CELL_BLANK, width: 82, height: 36, margin: 4, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t('results.tenPointsPoppers')}
                </div>
                <div style={CELL}>{scoreMode === 'steel-no-miss' ? '' : (!time ? '' : (numTenPoints - tenPointsPoppers) || '')}</div>
                {scoreMode === 'steel-no-miss' && (
                  <div style={CELL}>{!time ? '' : (numTenPoints - tenPointsPoppers) || ''}</div>
                )}
              </div>
            )}

            {/* Per-target rows */}
            {Array.from({ length: numPaper }).map((_, i) => {
              const score = scorePerTarget[i] || '';
              const a = (score.match(/A/g) || []).length;
              const c = (score.match(/C/g) || []).length;
              const d = (score.match(/D/g) || []).length;
              const m = score.split('').filter((ch) => ch === 'M').length;
              const bm = score.split('').filter((ch) => ch === 'm').length;
              return (
                <div key={i} style={{ display: 'flex' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, margin: 2 }}>
                    T{i + 1}
                  </div>
                  <div style={CELL}>{a || ''}</div>
                  <div style={CELL}>{c || ''}</div>
                  <div style={CELL}>{d || ''}</div>
                  <div style={CELL}>{m || ''}</div>
                  <div style={CELL}>{bm || ''}</div>
                </div>
              );
            })}
          </div>

          {/* Right: Match & shooter info */}
          <div style={{ marginLeft: 30 }}>
            <div style={{ fontSize: 13, fontWeight: 300, marginTop: 6 }}>{formTime}</div>
            <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}><u>{t('scoring.matchInfo')}</u></div>
            <div style={{ fontSize: 13, fontWeight: 900 }}>{match?.title || ''}</div>
            <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}>
              <u>{t('results.stageN', { n: stageIdx })}</u>
            </div>
            <div style={{ fontSize: 13, fontWeight: 300 }}>
              {t('scoring.ro')}:<b>{user?.name || user?.email || ''}</b>
            </div>
            <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}><u>{t('scoring.shooterInfo')}</u></div>
            <div style={{ fontSize: 13, fontWeight: 300 }}>
              {shooter.publicId} / <b>{shooter.name}</b>
            </div>
            <div style={{ fontSize: 13, fontWeight: 300 }}>{shooterInfo}</div>
          </div>
        </div>

        {/* Summary section */}
        <div style={{ opacity: isDq ? 0.5 : 1 }}>
          {/* Divider line */}
          <div style={{ margin: '6px 45px', width: 229, borderBottom: '2px solid black' }} />

          {/* Totals row: A C D M (M) gap PT Proc */}
          <div style={{ marginLeft: 45 }}>
            <div style={{ ...SUMMARY_CELL, borderLeft: '2px solid black' }}>{summary?.alphas || ''}</div>
            <div style={SUMMARY_CELL}>{summary?.charlies || ''}</div>
            <div style={{ ...SUMMARY_CELL, width: 45 }}>{summary?.deltas || ''}</div>
            <div style={SUMMARY_CELL}>{summary?.misses || ''}</div>
            <div style={SUMMARY_CELL}>{summary?.bonusMisses || ''}</div>
            <div style={{ ...SUMMARY_CELL, borderTop: 'none', borderBottom: 'none' }} />
            <div style={SUMMARY_CELL}>{noShoots || ''}</div>
            <div style={SUMMARY_CELL}>{procTotal || ''}</div>
          </div>

          {/* Column labels under totals */}
          <div style={{ display: 'flex' }}>
            <div style={CELL_BLANK} />
            <div style={HEADER}>A</div>
            <div style={HEADER}>{cLabel}</div>
            <div style={HEADER}>D</div>
            <div style={HEADER}>M</div>
            <div style={HEADER}>(M)</div>
            <div style={{ ...HEADER, width: 39 }} />
            <div style={HEADER}>PT</div>
            <div style={HEADER}>Proc</div>
          </div>

          {/* Penalty breakdown */}
          {procTotal > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginLeft: 45 }}>
              <div style={{ fontSize: '0.8em', fontWeight: 800 }}>{t('results.penalties')}:&nbsp;</div>
              <div style={{ fontSize: '0.8em' }}>
                {Object.keys(penaltiesByType).filter((r) => !!penaltiesByType[r]).map((r) => `${r} (${penaltiesByType[r]})`).join(' | ')}
              </div>
            </div>
          )}

          {/* Bonus field */}
          {stage.bonusField && (
            <div style={{ marginLeft: 45, marginTop: 24 }}>
              <div style={{ display: 'block', textAlign: 'center', lineHeight: '40px', width: 88, height: 40, border: '2px solid black' }}>
                {bonusField}
              </div>
              <div style={{ ...CAPTION, width: 88 }}>{stage.bonusField}</div>
            </div>
          )}

          {/* Bottom summary: rounds | time | [special penalty] | total | factor */}
          <div style={{ marginLeft: 45, display: 'flex', marginTop: 24 }}>
            <SummaryBottomCell
              value={isDq ? '' : `${(summary?.alphas || 0) + (summary?.charlies || 0) + (summary?.deltas || 0) + (summary?.misses || 0) + (summary?.bonusMisses || 0)} / ${stage.rounds}`}
              color={!isDq && summary && ((summary.alphas + summary.charlies + summary.deltas + summary.misses + summary.bonusMisses) !== stage.rounds) ? '#f50057' : '#000'}
              first
            />
            <SummaryBottomCell value={isDq ? '' : (summary?.timeStr || pad(time, 2))} />
            {specialPenalty > 0 && (
              <SummaryBottomCell value={`-${summary?.specialPenaltyValue || 0}`} />
            )}
            <SummaryBottomCell value={isDq ? '' : (summary?.total ?? '')} />
            <SummaryBottomCell value={isDq ? '' : (summary?.factorStr || '')} />
          </div>
          <div style={{ marginLeft: 45, display: 'flex' }}>
            <SummaryBottomLabel
              label={t('scoring.totalRoundsLabel')}
              color={!isDq && summary && ((summary.alphas + summary.charlies + summary.deltas + summary.misses + summary.bonusMisses) !== stage.rounds) ? '#f50057' : '#000'}
            />
            <SummaryBottomLabel label={t('results.time')} />
            {specialPenalty > 0 && (
              <SummaryBottomLabel label={t('scoring.specialPenalty', { specialPenalty })} />
            )}
            <SummaryBottomLabel label={t('scoring.totalPoints')} />
            <SummaryBottomLabel label={t('results.factor')} />
          </div>
        </div>

        {/* Warning about misses/penalties */}
        {missWarning && (
          <div style={{ marginTop: 12, padding: 12, background: '#fff3e0', borderRadius: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>{missWarning}</Typography>
          </div>
        )}

        {/* Signatures */}
        <div style={{ marginTop: 12 }}>
          <SignatureCapture label={t('scoring.roSignature')} value={roSignature} onEnd={setRoSignature} />
          {!isDq && (
            <Box sx={{ mt: 2 }}>
              <SignatureCapture
                label={`${t('scoring.signature')} - ${shooter.name}`}
                value={shooterSignature}
                onEnd={setShooterSignature}
              />
            </Box>
          )}
        </div>
      </div>
      )}

      {/* Submit */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained" size="large" onClick={handleSubmit} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          sx={{ px: 6 }}
        >
          {t('scoring.saveAndSendEmail')}
        </Button>
      </Box>

      {/* Temporary save */}
      {!isDq && (
        <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Typography
            variant="caption" color="error" sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleTempSave}
          >
            {t('scoring.saveTemporary')}
          </Typography>
        </Box>
      )}

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })} />
    </Box>
  );
}

function SummaryBottomCell({ value, color, first }) {
  return (
    <div style={{
      display: 'block', textAlign: 'center', lineHeight: '40px',
      width: 88, height: 40, color: color || '#000',
      borderRight: '2px solid black', borderTop: '2px solid black', borderBottom: '2px solid black',
      ...(first ? { borderLeft: '2px solid black' } : {}),
    }}>
      {value}
    </div>
  );
}

function SummaryBottomLabel({ label, color }) {
  return (
    <div style={{ ...CAPTION, width: 88, color: color || '#000' }}>
      {label}
    </div>
  );
}
